import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useGetOperationalStatus } from "@/hooks/useOperationalStatus";
import { APPLICATION_CATEGORY, PATH } from "@/lib/enums";
import { cn } from "@/lib/utils";
import {
  AppWindow,
  Building2,
  CreditCard,
  Database,
  Globe,
  LayoutDashboard,
  Layers,
  LogOut,
  Menu,
  Settings,
  Users,
  X,
  Terminal,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ProfileAvatar } from "../Common/ProfileAvatar";

type NavItem = {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
  match?: (pathname: string, search: string) => boolean;
};

const navigation: NavItem[] = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  {
    name: "Static sites",
    href: `${PATH.APPLICATIONS}?category=${APPLICATION_CATEGORY.STATIC_SITES}`,
    icon: Globe,
    match: (pathname, search) => {
      const params = new URLSearchParams(search);
      return (
        pathname === PATH.APPLICATIONS &&
        params.get("category") === APPLICATION_CATEGORY.STATIC_SITES
      );
    },
  },
  {
    name: "Applications",
    href: PATH.APPLICATIONS,
    icon: AppWindow,
    match: (pathname, search) => {
      if (pathname !== PATH.APPLICATIONS) return false;
      const params = new URLSearchParams(search);
      const category = params.get("category");
      return !category || category === APPLICATION_CATEGORY.APPLICATIONS;
    },
  },
  { name: "Projects", href: PATH.PROJECTS, icon: Layers },
  {
    name: "Databases",
    href: `${PATH.APPLICATIONS}?category=${APPLICATION_CATEGORY.DATABASES}`,
    icon: Database,
    match: (pathname, search) => {
      const params = new URLSearchParams(search);
      return (
        pathname === PATH.APPLICATIONS &&
        params.get("category") === APPLICATION_CATEGORY.DATABASES
      );
    },
  },
  { name: "Organization", href: "/organizations", icon: Building2 },
  { name: "Teams", href: "/teams", icon: Users },
  { name: "Plans & Billing", href: "/plans", icon: CreditCard },
  { name: "Settings", href: "/settings", icon: Settings },
];

export function Sidebar() {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const statusQuery = useGetOperationalStatus();
  const navigate = useNavigate();

  const activeCategory = useMemo(() => {
    if (location.pathname !== PATH.APPLICATIONS) {
      return "";
    }
    const params = new URLSearchParams(location.search);
    return params.get("category") ?? APPLICATION_CATEGORY.APPLICATIONS;
  }, [location.pathname, location.search]);

  return (
    <>
      {!isMobileMenuOpen && (
        <div className="lg:hidden fixed top-4 left-4 z-50">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleMobileMenu}
            className="text-[var(--primary)] hover:text-[var(--primary)] hover:bg-[var(--bg-alt)] border border-[var(--primary)]/30 h-12 w-12 p-0 flex items-center justify-center bg-[var(--bg)]"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div>
      )}

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div
        className={cn(
          "flex h-full flex-col border-r border-[var(--border-color)] transition-transform duration-300 ease-in-out bg-[var(--bg)]",
          "w-72 lg:w-64 fixed lg:static z-50",
          isMobileMenuOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
        )}
      >
        {isMobileMenuOpen && (
          <div className="lg:hidden flex justify-end p-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-[var(--primary)] hover:text-[var(--primary)] hover:bg-[var(--bg-alt)] border border-[var(--primary)]/30 h-12 w-12 p-0 flex items-center justify-center"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </Button>
          </div>
        )}

        {/* Logo Section - Cyberpunk Header */}
        <div className="flex flex-col px-6 py-8 border-b border-[var(--border-color)] flex-shrink-0 bg-[var(--bg)] relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[var(--primary)] to-transparent opacity-50"></div>
          <button
            className="text-xl font-bold font-mono tracking-tighter text-[var(--text-main)] hover:text-[var(--primary)] transition-colors duration-200 flex items-center gap-2"
            onClick={() => {
              navigate(PATH.HOME);
              setIsMobileMenuOpen(false);
            }}
          >
            <Terminal className="w-5 h-5 text-[var(--primary)]" />
            VIBE_DEPLOY
          </button>
          {activeCategory && (
            <div className="flex items-center gap-2 mt-2 pl-1">
              <div className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-pulse"></div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-subtle)] font-mono">
                {activeCategory.replace("_", " ")}
              </p>
            </div>
          )}
        </div>

        {/* User Profile - Compact Terminal Style */}
        <div className="px-4 py-4 border-b border-[var(--border-color)] flex-shrink-0">
          <div className="flex items-center gap-3 p-2 rounded border border-transparent hover:border-[var(--border-color)] hover:bg-[var(--bg-alt)] transition-all cursor-default">
            <div className="relative h-8 w-8 rounded-none border border-[var(--primary)]/50 flex items-center justify-center flex-shrink-0 bg-[var(--bg-alt)]">
              <ProfileAvatar profileImageUrl={user?.photoURL} />
            </div>
            <div className="min-w-0 font-mono">
              <p className="text-xs font-semibold text-[var(--text-main)] truncate">
                {user?.displayName}
              </p>
              <p className="text-[10px] text-[var(--text-subtle)] truncate opacity-70">
                USR: {user?.email?.split("@")[0]}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col">
          <nav className="flex-1 px-2 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              // Use referrer path/search if available
              let checkPath = location.state?.from || location.pathname;
              let checkSearch = location.state?.search || location.search;

              if (location.pathname.startsWith("/applications/")) {
                const storedCategory = location.state?.category;
                if (storedCategory) {
                  checkPath = PATH.APPLICATIONS;
                  checkSearch = `category=${storedCategory}`;
                }
              }

              const isActive = item.match
                ? item.match(checkPath, checkSearch)
                : checkPath === item.href;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 text-xs font-mono uppercase tracking-wide transition-all duration-200 border-l-2 group",
                    isActive
                      ? "border-[var(--primary)] bg-[var(--primary)]/5 text-[var(--primary)]"
                      : "border-transparent text-[var(--text-subtle)] hover:text-[var(--text-main)] hover:bg-[var(--bg-alt)] hover:border-[var(--text-subtle)]/50",
                  )}
                >
                  <item.icon
                    className={cn(
                      "h-4 w-4 transition-colors duration-200 flex-shrink-0",
                      isActive
                        ? "text-[var(--primary)]"
                        : "text-[var(--text-subtle)] group-hover:text-[var(--text-main)]",
                    )}
                  />
                  <span className="truncate">{item.name}</span>
                  {isActive && (
                    <span className="ml-auto w-1.5 h-1.5 bg-[var(--primary)] animate-pulse rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* System Status - HUD Style */}
          <div className="px-4 py-4 border-t border-[var(--border-color)]">
            <div className="p-3 bg-[var(--bg-alt)] border border-[var(--border-color)] text-[10px] font-mono space-y-2 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-1">
                <div className="w-1 h-1 bg-[var(--success)] rounded-full animate-ping"></div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-[var(--text-subtle)]">SYS.STATUS</span>
                <span className="text-[var(--success)]">
                  {statusQuery.data.status}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[var(--text-subtle)]">UPTIME</span>
                <span className="text-[var(--primary)]">
                  {statusQuery.data.uptime}%
                </span>
              </div>

              {/* Decorative scanline */}
              <div className="w-full h-px bg-[var(--border-color)] mt-2"></div>
              <div className="text-[var(--text-subtle)] opacity-50 text-[9px] pt-1">
                VIBE_OS v2.4.0
              </div>
            </div>
          </div>

          <div className="px-4 py-4 border-t border-[var(--border-color)]">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-[var(--text-subtle)] hover:text-[var(--error)] hover:bg-[var(--error)]/10 border border-transparent hover:border-[var(--error)]/30 text-xs font-mono uppercase"
              onClick={signOut}
            >
              <LogOut className="h-4 w-4" />
              TERMINATE_SESSION
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
